import { DrawApi } from '@apply/api';
import { HeaderPanelLeft } from './HeaderPanelLeft';
import { HeaderPanelCenter } from './HeaderPanelCenter/index';
import { HeaderPanelDrawColumn } from './HeaderPanelDrawColumn';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Dispatch, SetStateAction } from 'react';
import { DrawWaiverType, DrawWaiverActionType } from '@apply/constants';
import React from 'react';
import { IDrawBudgetApproval } from '@apply/api/src/api/DrawApi';

interface IDrawHeaderParameters {
    drawBudget: DrawApi.IDrawBudget | undefined;
    isAdmin: boolean;
    anyAcquisitionAmount: boolean;
    isEquityMet: boolean;
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    latestDrawFundedTotal: number;
    latestDrawBorrowerRequestTotal: number;
    latestDrawCreatedDate: Date | undefined;
    set_drawBudget: Dispatch<SetStateAction<DrawApi.IDrawBudget | undefined>>;
    showFeedback: (value: boolean) => void;
    finalizeSnackbar: (value: boolean) => void;
}

export const DrawHeader: React.FC<IDrawHeaderParameters> = ({
    drawBudget,
    set_drawBudget,
    isAdmin,
    anyAcquisitionAmount,
    isEquityMet,
    applicationInsights,
    borrowerName,
    latestDrawFundedTotal,
    latestDrawBorrowerRequestTotal,
    latestDrawCreatedDate,
    showFeedback,
    finalizeSnackbar,
}) => {
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const onDrawBorrowerRequestedChanged = (id, date) => {
        if (!drawBudget) return;
        if (!date) return; // borrowerRequested cannot be null

        drawApi.drawBorrowerRequested(id, date);

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                return { ...draw, borrowerRequested: date };
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawFundedOnChanged = (id, date) => {
        if (!drawBudget) return;

        drawApi.drawFundedOn(id, date);

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                return { ...draw, fundedOn: date };
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawInspectionOrderedChanged = (id, date) => {
        if (!drawBudget) return;

        drawApi.drawInspectionOrdered(id, date);

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                return { ...draw, inspectionOrdered: date };
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawInspectedOnChanged = (id, date) => {
        if (!drawBudget) return;

        drawApi.drawInspectedOn(id, date);

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                return { ...draw, inspectedOn: date };
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawNotesChanged = (id, notes) => {
        if (!drawBudget) return;

        drawApi.drawNotes(id, notes);

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                return { ...draw, notes: notes };
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawFinalized = (id: number) => {
        if (!drawBudget) return;

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                return { ...draw, isFinalized: true };
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawBorrowerSubmitted = async (id) => {
        if (!drawBudget) return;

        const updatedDraws = drawBudget.draws.map((draw) => {
            if (draw.id === id) {
                if (draw.needsBorrowerSignature) {
                    return {
                        ...draw,
                        borrowerSubmitted: true,
                        needsBorrowerSignature: false,
                        hasResubmitted: true,
                    };
                } else {
                    return {
                        ...draw,
                        borrowerSubmitted: true,
                        needsBorrowerSignature: false,
                    };
                }
            } else {
                return { ...draw };
            }
        });

        set_drawBudget({ ...drawBudget, draws: updatedDraws });
    };

    const onDrawDeleted = async (drawId) => {
        if (!drawBudget) return;

        const response = await drawApi.deleteDraw(drawId);

        if (response.statusCode !== 200) {
            alert(response.errors?.[0] || response.statusCode);
            return;
        }

        if (!response.result) return;

        set_drawBudget(response.result);
    };

    const onDrawOOBAccomodationWaiverChanged = async (drawId, isWaived) => {
        if (!drawBudget) return;
        if (isWaived) {
            const response = await drawApi.setDrawWaiver({
                drawId: drawId,
                drawWaiverType: DrawWaiverType.OutOfBalanceWaiver,
                drawWaiverActionType: DrawWaiverActionType.OutOfBalanceRemoveWaiver,
                isOutOfBalanceWaived: false,
            });
            if (response.statusCode !== 200) {
                alert(response.errors[0]);
                return;
            } else {
                const updatedDraws = drawBudget.draws.map((draw) => {
                    if (draw.id === drawId) {
                        return { ...draw, isOutOfBalanceWaived: false };
                    } else {
                        return { ...draw };
                    }
                });

                set_drawBudget({ ...drawBudget, draws: updatedDraws, isOutOfBalanceWaived: false });
            }
        } else {
            const response = await drawApi.setDrawWaiver({
                drawId: drawId,
                drawWaiverType: DrawWaiverType.OutOfBalanceWaiver,
                drawWaiverActionType: DrawWaiverActionType.OutOfBalanceWaived,
                isOutOfBalanceWaived: true,
            });
            if (response.statusCode !== 200) {
                alert(response.errors[0]);
                return;
            } else {
                const updatedDraws = drawBudget.draws.map((draw) => {
                    if (draw.id === drawId) {
                        return { ...draw, isOutOfBalanceWaived: true };
                    } else {
                        return { ...draw };
                    }
                });

                set_drawBudget({ ...drawBudget, draws: updatedDraws, isOutOfBalanceWaived: true });
            }
        }
    };

    const onEquityRequirementChange = (newValue: number) => {
        if (!drawBudget) return;

        drawApi.drawBudgetEquityRequirement(drawBudget.id, !newValue ? 0 : newValue);
        set_drawBudget({ ...drawBudget, equityRequirement: newValue });
    };

    const onEquityMetOnChange = (newValue: Date) => {
        if (!drawBudget) return;

        drawApi.drawBudgetEquityMetOn(drawBudget.id, newValue);
        set_drawBudget({ ...drawBudget, equityMetOn: newValue });
    };

    const onBankAccountLast4DigitsOnChange = (newValue: string) => {
        if (!drawBudget) return;

        drawApi.drawBudgetBankAccountLast4DigitsOn(drawBudget.id, newValue);
        set_drawBudget({ ...drawBudget, bankAccountLast4Digits: newValue });
    };

    const onApprovalAdd = async () => {
        if (!drawBudget) return;

        const data = await drawApi.addApproval(drawBudget.id);

        const newApprovals: IDrawBudgetApproval[] = [];
        newApprovals.push(data.result);
        drawBudget.approvals.forEach((item) => newApprovals.push(item));

        set_drawBudget({
            ...drawBudget,
            currentApproval: data.result,
            approvals: newApprovals,
        });
    };

    return (
        <div className='draw-header'>
            <HeaderPanelLeft
                {...{
                    ...drawBudget,
                    drawTotal: latestDrawFundedTotal ? latestDrawFundedTotal : latestDrawBorrowerRequestTotal,
                    latestDrawCreatedDate: latestDrawCreatedDate,
                }}
            />
            <HeaderPanelCenter
                onEquityRequirementChange={onEquityRequirementChange}
                onEquityMetOnChange={onEquityMetOnChange}
                onBankAccountLast4DigitsOnChange={onBankAccountLast4DigitsOnChange}
                onApprovalAdd={onApprovalAdd}
                drawBudget={drawBudget}
                isAdmin={isAdmin}
                anyAcquisitionAmount={anyAcquisitionAmount}
            />
            {drawBudget?.draws?.map((draw) => (
                <HeaderPanelDrawColumn
                    key={draw.drawNumber}
                    draw={draw}
                    currentApproval={drawBudget?.currentApproval}
                    isAdmin={isAdmin}
                    isEquityMet={isEquityMet}
                    isInspectionRequireFirst={draw?.isInspectionRequireFirst}
                    applicationInsights={applicationInsights}
                    borrowerName={borrowerName}
                    latestDrawFundedTotal={latestDrawFundedTotal}
                    latestDrawBorrowerRequestTotal={latestDrawBorrowerRequestTotal}
                    onDrawBorrowerRequestedChanged={onDrawBorrowerRequestedChanged}
                    onDrawFundedOnChanged={onDrawFundedOnChanged}
                    onDrawInspectionOrderedChanged={onDrawInspectionOrderedChanged}
                    onDrawInspectedOnChanged={onDrawInspectedOnChanged}
                    finalizeSnackbar={finalizeSnackbar}
                    onDrawNotesChanged={onDrawNotesChanged}
                    onDrawFinalized={onDrawFinalized}
                    onDrawBorrowerSubmitted={onDrawBorrowerSubmitted}
                    showFeedback={showFeedback}
                    onDrawDeleted={onDrawDeleted}
                    onDrawOOBAccomodationWaiverChanged={onDrawOOBAccomodationWaiverChanged}
                />
            ))}
        </div>
    );
};
